import { cx } from '@emotion/css';
import { AssignmentTwoTone, InfoOutlined } from '@mui/icons-material';
import { Divider, Tooltip, useMediaQuery } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { useEffect, useState } from 'react';

import { CtaBox } from '_components/common/CtaBox';
import { ToggleButton } from '_components/common/ToggleButton';
import { usePublicAppContext } from '_core/context/public-app-context';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import * as S from '_features/AgencyProfile/components/ServicesPanel/styles';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';
import { useServicePages } from '_features/AgencyProfile/hooks/useServicePages';
import type { Section } from '_features/AgencyProfile/utils';

import { ServiceCard } from './ServiceCard';
import type { ServiceNode } from './types';

interface Props {
  className?: string;
  handleActivePanel: (section?: Section) => void;
}

const getTotalWorkCount = (services: ServiceNode[]): number => {
  return services.reduce((total, service) => total + service.works.totalCount, 0);
};

export const maxNumberOfServicesDisplayed = 10;

export const ServicesPanel = (props: Props) => {
  const { className, handleActivePanel } = props;
  const { media_legacy } = useMediaQuery();
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { locale, pageData, domainInfo } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });
  const { data: servicePages } = useServicePages(agencySlug, locale as string, domainInfo?.getOriginUrl() ?? '');
  const { name } = agency ?? {};

  const services = (agency?.services.nodes ?? []).filter((s) => s != null);
  const servicesCount = services.length;

  const [toggledAllExpertises, setToggledAllExpertises] = useState(false);

  const handleToggledAllExpertises = () => {
    setToggledAllExpertises(!toggledAllExpertises);
  };

  useEffect(() => {
    if (media_legacy.xs) {
      setToggledAllExpertises(true);
    }
  }, [media_legacy]);

  const getServiceUrl = (service: ServiceNode) => {
    const expertiseSlug = service.expertise.slug;
    const url = servicePages?.find((page) => page.expertiseSlug === expertiseSlug)?.page?.url;
    return url != null && url != '' ? `${url}` : `${domainInfo?.getLocalizedUrl(locale)}/${expertiseSlug}`;
  };

  return (
    <SectionBox
      id="services"
      className={cx(className, 'sm:py-24 max-sm:py-16')}
      title={t('agency:profile.services.title')}
      subtitle={
        <Trans
          i18nKey={'agency:profile.services.subtitle'}
          count={servicesCount}
          values={{ count: servicesCount, name }}>
          <b>{'{{count}}'} services</b>
          offered by {'{{name}}'}
        </Trans>
      }>
      {servicesCount > 0 ? (
        <div className="lg:px-16 sm:px-8">
          <div className="layout-row py-2 pr-4 pl-6 hide show-gt-sm">
            <span
              title={t('agency:profile.services.serviceName')}
              className="small text-secondary-900 flex-1 px-4 text-truncate">
              {t('agency:profile.services.serviceName')}
            </span>
            <div className="flex-20 mx-4 layout-row layout-align-start-center">
              <span
                title={t('agency:profile.services.experienceLevel.label')}
                className="small  text-truncate text-secondary-900">
                {t('agency:profile.services.experienceLevel.label')}
              </span>
              <Tooltip
                title={
                  <div className="layout-column">
                    <span className="bold mb-2 p">{t('agency:profile.services.experienceLevel.title')}</span>
                    <p className="small">
                      <Trans i18nKey={'agency:profile.services.experienceLevel.description'}>
                        The experience helps you to assess {{ agencyName: name }} level of expertise calculated based on
                        the absolute number of works delivered for a specific service but also in comparison with the
                        number of works in other services offered by the agency.
                      </Trans>
                    </p>
                  </div>
                }>
                <InfoOutlined className="m-1 text-secondary-900" style={{ fontSize: 14 }} />
              </Tooltip>
            </div>
            <Divider />
            <span
              title={t('agency:profile.services.relatedReviews')}
              className="small text-secondary-900 flex-15 px-4 text-truncate">
              {t('agency:profile.services.relatedReviews')}
            </span>
            <Divider />
            <span
              title={t('agency:profile.services.startingBudget')}
              className="small text-secondary-900 flex-15 mr-12 px-4 text-truncate">
              {t('agency:profile.services.startingBudget')}
            </span>
          </div>
          <S.Shape className="rounded-lg list-reset overflow-hidden shadow-2 layout-column bg-secondary-50 border border-secondary-700">
            {(servicesCount > maxNumberOfServicesDisplayed && !toggledAllExpertises
              ? services.slice(0, maxNumberOfServicesDisplayed)
              : services
            ).map((service, index) => (
              <ServiceCard
                index={index}
                visibleServicesCount={maxNumberOfServicesDisplayed}
                key={service.expertise.name}
                agencySlug={agencySlug}
                service={service}
                serviceUrl={getServiceUrl(service)}
                servicesCount={servicesCount}
                handleActivePanel={handleActivePanel}
                totalWorksCount={getTotalWorkCount(services)}
              />
            ))}
            {servicesCount > maxNumberOfServicesDisplayed && !media_legacy.xs ? (
              <ToggleButton
                handleToggled={() => handleToggledAllExpertises()}
                seeMoreBtnLabel={
                  <Trans
                    i18nKey={'agency:profile.services.seeOtherServices'}
                    count={servicesCount - maxNumberOfServicesDisplayed}>
                    See all other services ({{ count: servicesCount - maxNumberOfServicesDisplayed }})
                  </Trans>
                }
                seeLessBtnLabel={t('agency:profile.services.seeLessServices')}
                toggled={toggledAllExpertises}
              />
            ) : null}
          </S.Shape>
          <footer className="p-8 layout-row layout-align-center-center hide-xs">
            <CtaBox
              title={t('agency:profile.services.cta.title')}
              subtitle={t('agency:profile.services.cta.subtitle', { name })}
              btnLabel={t('agency:profile.services.cta.btnLabel')}
              icon={<AssignmentTwoTone />}
              element="services-panel"
              agencySlug={agencySlug}
            />
          </footer>
        </div>
      ) : null}
    </SectionBox>
  );
};
